import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Row,
  Col,
  notification,
  Dropdown,
  Avatar,
  Tabs,
  Checkbox,
  Divider
} from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  UserOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  CopyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CrownOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { adminApi } from '../services/platformApi';

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Role + permission strings below must match the backend enums verbatim
// (platform/src/auth/models.py — UserRole, Permission) since HF1 wires
// require_admin()/role checks against those exact values.
interface User {
  id: string;
  username: string;
  email: string;
  full_name: string;
  role: 'admin' | 'manager' | 'classify_user' | 'foia_user';
  is_active: boolean;
  created_at: string;
  last_login?: string;
  api_key?: string;
  permissions: Record<string, boolean>;
  metadata?: Record<string, any>;
}


const AVAILABLE_PERMISSIONS = {
  // Classification permissions
  'classify_documents': 'Classify Documents',
  'tearline_sanitize': 'Tearline Sanitize',

  // FOIA permissions
  'foia_process': 'FOIA Process',

  // Admin permissions
  'manage_configs': 'Manage Configs',
  'manage_users': 'Manage Users',
  'manage_scg': 'Manage SCG',

  // Document management
  'upload_documents': 'Upload Documents',
  'view_documents': 'View Documents'
};

const UserManager: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await adminApi.listUsers();
      setUsers(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to load users'
      });
      setUsers([]);
      setLoading(false);
    }
  };

  const handleCreateUser = async (values: any) => {
    try {
      const newUser = await adminApi.createUser(values);
      setUsers([...users, newUser]);
      setModalVisible(false);
      form.resetFields();
      notification.success({
        message: 'Success',
        description: 'User created successfully'
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to create user'
      });
    }
  };

  const handleUpdateUser = async (values: any) => {
    if (!editingUser) return;

    try {
      const updateData: any = {
        email: values.email,
        full_name: values.full_name,
        role: values.role,
        permissions: values.permissions || {}
      };

      if (values.password) {
        updateData.password = values.password;
      }

      await adminApi.updateUser(editingUser.id, updateData);
      await loadUsers();
      setModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      notification.success({
        message: 'Success',
        description: 'User updated successfully'
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to update user'
      });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await adminApi.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      notification.success({
        message: 'Success',
        description: 'User deleted successfully'
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to delete user'
      });
    }
  };

  const handleResetPassword = async (userId: string) => {
    try {
      const data = await adminApi.resetPassword(userId);
      Modal.info({
        title: 'Password Reset',
        content: `Temporary password: ${data.temporary_password}`,
        width: 400
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to reset password'
      });
    }
  };

  const handleRegenerateApiKey = async (userId: string) => {
    try {
      const data = await adminApi.regenerateApiKey(userId);
      await loadUsers();
      Modal.info({
        title: 'New API Key',
        content: (
          <div>
            <p>New API key generated:</p>
            <Input.TextArea
              value={data.api_key}
              readOnly
              rows={2}
              style={{ marginTop: 8 }}
            />
            <Button
              icon={<CopyOutlined />}
              onClick={() => navigator.clipboard.writeText(data.api_key)}
              style={{ marginTop: 8 }}
            >
              Copy to Clipboard
            </Button>
          </div>
        ),
        width: 500
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.detail || 'Failed to regenerate API key'
      });
    }
  };

  const openEditModal = (user?: User) => {
    setEditingUser(user || null);
    if (user) {
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        permissions: user.permissions || {}
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <CrownOutlined />;
      case 'manager':
        return <TeamOutlined />;
      case 'classify_user':
        return <UserOutlined />;
      case 'foia_user':
        return <EyeOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'manager':
        return 'blue';
      case 'classify_user':
        return 'geekblue';
      case 'foia_user':
        return 'default';
      default:
        return 'default';
    }
  };

  const getActionMenu = (user: User): MenuProps => ({
    items: [
      {
        key: 'reset-password',
        icon: <LockOutlined />,
        label: 'Reset Password',
        onClick: () => handleResetPassword(user.id)
      },
      {
        key: 'regenerate-api',
        icon: <KeyOutlined />,
        label: 'Regenerate API Key',
        onClick: () => handleRegenerateApiKey(user.id)
      },
      {
        type: 'divider'
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Delete User',
        danger: true,
        onClick: () => handleDeleteUser(user.id)
      }
    ]
  });

  const columns: ColumnsType<User> = [
    {
      title: 'User',
      key: 'user',
      render: (_: any, user: User) => (
        <Space>
          <Avatar style={{ backgroundColor: '#1890ff' }}>
            {user.full_name[0]?.toUpperCase()}
          </Avatar>
          <div>
            <div>{user.full_name}</div>
            <Text type="secondary">@{user.username}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Role',
      key: 'role',
      render: (_: any, user: User) => (
        <Tag icon={getRoleIcon(user.role)} color={getRoleColor(user.role)}>
          {user.role}
        </Tag>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, user: User) => (
        <Tag color={user.is_active ? 'green' : 'default'}>
          {user.is_active ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Created',
      key: 'created',
      render: (_: any, user: User) => new Date(user.created_at).toLocaleDateString()
    },
    {
      title: 'Last Login',
      key: 'last_login',
      render: (_: any, user: User) => user.last_login 
        ? new Date(user.last_login).toLocaleDateString() 
        : 'Never'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, user: User) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(user)}
          />
          <Dropdown menu={getActionMenu(user)} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ];

  return (
    <Card
      title={
        <Space>
          <UserOutlined />
          User Management
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openEditModal()}
        >
          Add User
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingUser ? 'Edit User' : 'Create New User'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingUser ? handleUpdateUser : handleCreateUser}
        >
          <Tabs defaultActiveKey="basic">
            <TabPane tab="Basic Info" key="basic">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Username"
                    name="username"
                    rules={[{ required: true, message: 'Please enter username' }]}
                  >
                    <Input 
                      prefix={<UserOutlined />} 
                      disabled={!!editingUser}
                      placeholder="Username"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Full Name"
                    name="full_name"
                    rules={[{ required: true, message: 'Please enter full name' }]}
                  >
                    <Input placeholder="Full Name" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Please enter email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input prefix={<MailOutlined />} placeholder="Email" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={editingUser ? 'New Password (leave empty to keep current)' : 'Password'}
                    name="password"
                    rules={editingUser ? [] : [{ required: true, message: 'Please enter password' }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Password"
                      iconRender={(visible: boolean) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Role"
                    name="role"
                    rules={[{ required: true, message: 'Please select role' }]}
                  >
                    <Select placeholder="Select role">
                      <Option value="admin">
                        <Space>
                          <CrownOutlined />
                          Admin
                        </Space>
                      </Option>
                      <Option value="manager">
                        <Space>
                          <TeamOutlined />
                          Manager
                        </Space>
                      </Option>
                      <Option value="classify_user">
                        <Space>
                          <UserOutlined />
                          Classify User
                        </Space>
                      </Option>
                      <Option value="foia_user">
                        <Space>
                          <EyeOutlined />
                          FOIA User
                        </Space>
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab="Permissions" key="permissions">
              <Title level={5}>User Permissions</Title>
              <Divider />
              
              <Row gutter={[16, 16]}>
                {Object.entries(AVAILABLE_PERMISSIONS).map(([key, label]) => (
                  <Col span={12} key={key}>
                    <Form.Item
                      name={['permissions', key]}
                      valuePropName="checked"
                    >
                      <Checkbox>{label}</Checkbox>
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </TabPane>
          </Tabs>

          <div style={{ textAlign: 'right', marginTop: 24 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </Card>
  );
};

export default UserManager;